// src/utils/acceptanceEmails.js
// Default subjects + bodies for post-acceptance emails. Mirrors the
// openHouseEmails.js pattern. UI consumes via acceptanceEmailComposer.

import {
  SELLER_WHATS_HAPPENED,
  SELLER_WHATS_NEXT_INTRO,
  SELLER_CLOSING_BLOCK,
  BUYER_EARNEST_MONEY_BLOCK,
  BUYER_INSPECTION_BODY,
  BUYER_MORTGAGE_GUIDANCE,
} from "./acceptanceEmailDefaults";

export const ACCEPTANCE_TEMPLATE_IDS = [
  "seller-client-accepted",
  "buyer-client-accepted",
  "title-new-transaction",
  "other-agent-intro",
];

export const ACCEPTANCE_TEMPLATE_META = {
  "seller-client-accepted": {
    category: "Post-Acceptance",
    label: "Seller Client — Offer Accepted",
    meta: "Sent to sellers right after an offer is accepted",
    auto: false,
  },
  "buyer-client-accepted": {
    category: "Post-Acceptance",
    label: "Buyer Client — Offer Accepted",
    meta: "Sent to buyers right after their offer is accepted",
    auto: false,
  },
  "title-new-transaction": {
    category: "Post-Acceptance",
    label: "Title — New Transaction",
    meta: "Sent to our title closer when a deal goes Under Contract",
    auto: false,
  },
  "other-agent-intro": {
    category: "Post-Acceptance",
    label: "Other Agent — Intro",
    meta: "TC introduces themselves to the other side's agent and lists the deadlines",
    auto: false,
  },
};

export const DEFAULT_ACCEPTANCE_TEMPLATES = {
  "seller-client-accepted": {
    subject: "Congrats on accepting an offer on {{propertyAddress}}!",
    body:
`Good Morning/Afternoon {{sellerNames}},

A big congratulations on accepting an offer on {{propertyAddress}}! We wanted to send over a copy of the fully executed Purchase Agreement for your records. We will be getting in touch each week to update you on what's happened and what is coming up. Here's the first update to get you up to speed with the start of this transaction!

${SELLER_WHATS_HAPPENED}

The inspection contingency ends on {{inspectionContingencyEnd}} at 11:59 PM.

${SELLER_WHATS_NEXT_INTRO}

You will be contacted by {{ourTitleCloserName}} from {{ourTitleCompany}} with closing information. We will check in with {{ourTitleCloserFirstName}} in a couple of weeks to make sure everything is progressing smoothly with your file.

${SELLER_CLOSING_BLOCK}

Best regards,`,
  },

  "buyer-client-accepted": {
    subject: "Congrats: your offer on {{propertyAddress}} was accepted!",
    body:
`Good Morning/Afternoon {{buyerNames}},

Congratulations on your offer being accepted! This step marks your home search ending and the Contract to Close process beginning. We will be sending you frequent updates with timeline check-ins and other relevant information regarding the purchase of your new home. I have attached a copy of the Purchase Agreement for your records.

${BUYER_EARNEST_MONEY_BLOCK}

INSPECTION CONTINGENCY

Date: Inspection Contingency Ends at {{inspectionContingencyEnd}} at 11:59 PM

We are working on scheduling / have scheduled the inspection with {{inspectorName}}. He will be conducting the inspection on {{inspectionDate}} with a walk-through with you and {{agentFirstName}} at {{walkthroughTime}}.

${BUYER_INSPECTION_BODY}

NEXT STEPS

You will be contacted by {{ourTitleCloserName}} from {{ourTitleCompany}} with closing information.

We have sent your lender a copy of your executed purchase agreement. {{lenderName}} will be in touch with next steps from their end and will update you throughout your loan process. {{lenderPronounSubject}} will order the appraisal once we are through our Inspection Contingency. The appraisal is usually received within 2 weeks of being ordered.

${BUYER_MORTGAGE_GUIDANCE}

Best regards,`,
  },

  "title-new-transaction": {
    subject: "New Transaction: {{agentName}}'s {{sideLabel}} at {{propertyAddress}}",
    body:
`Hi {{ourTitleCloserFirstName}},

I've attached the Purchase Agreement for {{agentName}}'s {{sideLabel}} at {{propertyAddress}}. The broker admin fee is \${{brokerAdminFee}} and the commission is {{totalCommissionPct}}% ({{buyerSideCommissionPct}}% to the buyer's agent).

{{partyLabel}}: {{clientNames}}
{{clientContactBlock}}
{{clientCurrentAddressLine}}

{{otherSideLabel}}'s closer:
{{otherSideTitleCompany}}
{{otherSideTitleCloserName}}
{{otherSideTitleCloserContact}}

Thanks,`,
  },

  // The TC's first note to the OTHER side's agent. [Agent Name] stays a literal
  // blank: nothing on a deal records who the co-op agent is, so there is no
  // token to merge (same house pattern as the transaction-sequence emails).
  // The earnest money date is a blank too — the deal stores when EM was
  // RECEIVED, not when it's due.
  // {{writtenStatementLine}} carries the whole sentence so it can disappear on
  // a cash deal rather than leaving a label with no date.
  "other-agent-intro": {
    subject: "New Transaction: {{propertyAddress}}",
    body:
`Good Morning/Afternoon [Agent Name],

We are excited to be working with you! I'd like to introduce myself as the Transaction Coordinator for Team Edelstein. Please copy me in all correspondence.

Below are a few details regarding this transaction:

Earnest Money: The earnest money should be submitted via TrustFunds no later than ______

Closing Date: {{closingDate}}

Inspection Deadline: {{inspectionContingencyEnd}}

{{writtenStatementLine}}

Closer/Title Company: For closing services the Sellers will be using:

{{ourTitleCompany}}
{{ourTitleCloserName}}
{{ourTitleCloserEmail}}
{{ourTitleCloserPhone}}

Please send us your closer's contact information. I have attached a copy of the Purchase Agreement. I look forward to working with you throughout this process! Please don't hesitate to contact me if you have any questions.

Best regards,`,
  },
};
